import { useCallback, useEffect, useState } from 'react'
import type { Step } from 'react-joyride'
import { useAuth } from '../contexts/AuthContext'
import { useRole } from '../contexts/RoleContext'
import { supabase } from '../lib/supabase'
import { theme } from '../theme/theme'

export interface CoachOnboardingStep extends Step {
  target: string
  content: React.ReactNode
  title?: string
  disableBeacon?: boolean
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center' | 'auto'
}

export const useCoachOnboarding = () => {
  const { user } = useAuth()
  const { currentRole } = useRole()
  const [run, setRun] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [loading, setLoading] = useState(true)

  // Define tour steps
  const steps: CoachOnboardingStep[] = [
    {
      target: 'body',
      content: "Let's take a quick tour to help you get started as a coach.",
      title: 'Welcome to supacoach! 🎉',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="invite-button"]',
      content: (
        <div>
          <p style={{ margin: 0 }}>
            Click here to invite athletes to your coaching program. There's a link you can share to sign up and connect
            with you.
          </p>
        </div>
      ),
      title: 'Invite Athletes',
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="add-self-link"]',
      content:
        'Want to test the platform? Click here to add yourself as an athlete. You can switch between coach and athlete views.',
      title: 'Test as an Athlete',
      placement: 'top',
      disableBeacon: true,
    },
    {
      target: '[data-tour="chat-button"]',
      content: (
        <div>
          <img src="/onboarding/chat.png" alt="Chat with Athletes" style={{ width: '250px', marginBottom: '12px' }} />
          <p style={{ margin: 0 }}>
            Chat with your athletes in real-time. Send text, images, videos, and files. <br />
            Create broadcast channels to send messages to multiple athletes at once. <br />
            Group chats are also available.
          </p>
        </div>
      ),
      title: 'Chat with Athletes',
      placement: 'top',
      disableBeacon: true,
    },
    {
      target: '[data-tour="tab-automations"]',
      title: 'Automate your Workflow',
      content: (
        <div>
          <img
            src="/onboarding/automate.png"
            alt="Chat with Athletes"
            style={{ width: '300px', marginBottom: '12px' }}
          />
          <p style={{ margin: 0 }}>Optimize your coaching workflow with custom automations. Manage them here.</p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="user-menu"]',
      content: 'Access your account settings, switch roles, and sign out from here.',
      title: 'User Menu',
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: 'body',
      content: (
        <div>
          <p>That's it! You're ready to start.</p>
          <p style={{ marginTop: 20 }}>
            Don't hesitate to contact us at{' '}
            <a href="mailto:hello@supacoach.ai" style={{ color: theme.colors.primary }}>
              hello@supacoach.ai
            </a>{' '}
            for any kind of questions or feedback.
          </p>
        </div>
      ),
      title: "You're All Set! 🚀",
      placement: 'center',
      disableBeacon: true,
    },
  ]

  // Check if user has completed onboarding
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null

    const checkOnboardingStatus = async () => {
      if (!user?.id) {
        setLoading(false)
        return
      }

      // Only show onboarding to coaches
      if (currentRole !== 'coach') {
        setLoading(false)
        return
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('onboarding_completed_coach')
          .eq('id', user.id)
          .single()

        if (error) {
          console.error('Error checking onboarding status:', error)
          setLoading(false)
          return
        }

        // Start tour if onboarding not completed (with 10 second delay)
        if (data && !data.onboarding_completed_coach) {
          timeoutId = setTimeout(() => {
            setRun(true)
          }, 10000) // 10 second delay
        }
      } catch (err) {
        console.error('Error in checkOnboardingStatus:', err)
      } finally {
        setLoading(false)
      }
    }

    checkOnboardingStatus()

    // Cleanup timeout on unmount
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [user?.id, currentRole])

  // Mark onboarding as completed
  const completeOnboarding = useCallback(async () => {
    if (!user?.id) return

    try {
      const { error } = await supabase.from('profiles').update({ onboarding_completed_coach: true }).eq('id', user.id)

      if (error) {
        console.error('Error completing onboarding:', error)
      }
    } catch (err) {
      console.error('Error in completeOnboarding:', err)
    }
  }, [user?.id])

  // Reset onboarding (for testing or if user wants to see tour again)
  const resetOnboarding = useCallback(async () => {
    if (!user?.id) return

    try {
      const { error } = await supabase.from('profiles').update({ onboarding_completed_coach: false }).eq('id', user.id)

      if (error) {
        console.error('Error resetting onboarding:', error)
      } else {
        setRun(true)
        setStepIndex(0)
      }
    } catch (err) {
      console.error('Error in resetOnboarding:', err)
    }
  }, [user?.id])

  // Start tour manually
  const startTour = useCallback(() => {
    setStepIndex(0)
    setRun(true)
  }, [])

  // Stop tour
  const stopTour = useCallback(() => {
    setRun(false)
  }, [])

  return {
    run,
    steps,
    stepIndex,
    loading,
    setStepIndex,
    setRun,
    completeOnboarding,
    resetOnboarding,
    startTour,
    stopTour,
  }
}
